import React from "react";
import { useQuery } from "react-query";
import { Box, Button, Card, CardMedia, Chip, Divider, Grid, Typography } from "@mui/material";
import { Bed, ErrorOutline, HelpOutline, Mail, Share, Square, WhatsApp } from "@mui/icons-material";
import { Bath, MapPin } from "lucide-react";
import { shareGeneric, shareViaEmail, shareViaWhatsApp } from "../components/FormUtility/FormUtility";
import { GetProjectsForUrl } from "../hooks/ProjectsDetails/UseProjectsDetails";
import { useSetting } from "../hooks/Hooks";
import { ImageSlider } from "../pages/projects/ProjectDetails";

const PublicProjectPage = ({ projectSlug }) => {

    console.log(projectSlug, "projectSlug")

    const { globalData } = useSetting();

    const {
        data: project,
        isLoading,
        isError,
        error,
    } = useQuery(["projectDetails", projectSlug], () => GetProjectsForUrl(projectSlug), {
        retry: 1,
        refetchOnWindowFocus: false,
    });
    console.log(project, "projectprojectprojectproject")
    if (isLoading) return <h3>Loading project details...</h3>;
    if (isError) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100vh"
                bgcolor="#fff3f3"
                padding={3}
            >
                <Card
                    sx={{
                        maxWidth: 480,
                        p: 4,
                        boxShadow: 6,
                        borderRadius: 3,
                        textAlign: "center",
                    }}
                >
                    <ErrorOutline color="error" sx={{ fontSize: 48, mb: 2 }} />
                    <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: "bold", color: "error.main" }}>
                        No Project Found
                    </Typography>

                    <Typography variant="subtitle1" gutterBottom sx={{ mb: 3 }}>
                        Oops! Something went wrong while fetching the project details.
                    </Typography>

                    {error?.suggestion ? (
                        <>
                            <Typography variant="body1" sx={{ fontWeight: "medium", mb: 1 }}>
                                Did you mean:
                            </Typography>

                            <Button
                                variant="outlined"
                                color="primary"
                                href={`/${error.suggestion}`}
                                startIcon={<HelpOutline />}
                                sx={{
                                    textTransform: "none",
                                    fontWeight: "bold",
                                    fontSize: "1rem",
                                    "&:hover": {
                                        backgroundColor: "primary.light",
                                        textDecoration: "underline",
                                    },
                                }}
                            >
                                {error.prj_name || error.suggestion}
                            </Button>
                        </>
                    ) : (
                        <Typography variant="body1" sx={{ fontWeight: "bold", color: "error.main" }}>
                            Project not found.
                        </Typography>
                    )}
                </Card>
            </Box>
        );
    }


    const DetailItem = ({ label, value, highlight }) => (
        <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
                {label}
            </Typography>
            <Typography
                variant={highlight ? "h6" : "body1"}
                fontWeight={highlight ? "bold" : "medium"}
                color={highlight ? "#1976d2" : "inherit"}
            >
                {value}
            </Typography>
        </Box>
    );

    const handleEmailShare = () => {
        if (!project) return;
        shareViaEmail({
            name: project.prj_name,
            location: `${project.prj_area_or_location}, ${project.prj_city}`,
            price: `₹${project.prj_start_price || "N/A"} Cr`,
        });
    };

    const handleWhatsAppShare = () => {
        if (!project) return;
        shareViaWhatsApp({
            name: project.prj_name,
            location: `${project.prj_area_or_location}, ${project.prj_city}`,
            price: `₹${project.prj_start_price || "N/A"} Cr`,
        });
    };

    const handleShare = () => {
        if (!project) return;
        shareGeneric({
            name: project.prj_name,
            location: `${project.prj_area_or_location}, ${project.prj_city}`,
        });
    };


    const PropertyStat = ({ icon: Icon, value }) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Icon size={22} color="#64748b" />
            <Typography variant="h6">{value}</Typography>
        </Box>
    );

    // Helper to fix path slashes
    const formatPath = (path) => path?.replace(/\\/g, "/")
    return (
        <Box
            sx={{
                px: { xs: 2, sm: 4, md: 8 },
                py: { xs: 4, sm: 6 },
                background: "#f9fafb",
                minHeight: "100vh",
            }}
        >
            <h1>{project.prj_name}</h1>
            <Card
                elevation={0}
                sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    background: "#fff",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
                    padding: 1,
                    mt: 2,
                }}
            >
                {project.project_files?.gallery?.length > 0 && (
                    <ImageSlider
                        images={project.project_files.gallery.map((img) =>
                            `${globalData.API_URL}/${formatPath(img.file_path)}`
                        )}
                    />
                )}

                <Box sx={{ py: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="h5" fontWeight="bold">
                                {project.prj_name}
                            </Typography>
                            <Chip
                                label={`#${project.prj_id}`}
                                size="small"
                                sx={{ backgroundColor: "#e3f2fd", color: "#1976d2" }}
                            />
                        </Box>
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                                startIcon={<Mail size={18} />}
                                onClick={handleEmailShare}
                                sx={{
                                    color: "#565a5b",
                                    padding: "6px 12px",
                                    fontWeight: 600,
                                    textTransform: "none",
                                    letterSpacing: "0.5px",
                                    borderRadius: 2,
                                    border: "1px solid #e0e0e0",
                                    "&:hover": {
                                        backgroundColor: "#f3f4f6",
                                    },
                                }}
                            >
                                Send Mail
                            </Button>
                            <Button
                                startIcon={<WhatsApp size={18} />}
                                onClick={handleWhatsAppShare}
                                sx={{
                                    color: "#fff",
                                    backgroundColor: "#128C7E",
                                    padding: "6px 12px",
                                    fontWeight: 600,
                                    textTransform: "none",
                                    letterSpacing: "0.5px",
                                    borderRadius: 2,
                                    border: "1px solid #e0e0e0",
                                    "&:hover": {
                                        backgroundColor: "#0d6b60ff",
                                    },
                                }}
                            >
                                WhatsApp
                            </Button>
                            <Button
                                startIcon={<Share size={18} />}
                                onClick={handleShare}
                                sx={{
                                    color: "#565a5b",
                                    padding: "6px 12px",
                                    fontWeight: 600,
                                    textTransform: "none",
                                    letterSpacing: "0.5px",
                                    borderRadius: 2,
                                    border: "1px solid #e0e0e0",
                                    "&:hover": {
                                        backgroundColor: "#f3f4f6",
                                    },
                                }}
                            >
                                Share
                            </Button>
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                        <MapPin size={18} color="#64748b" />
                        <Typography variant="body1" color="text.secondary">
                            {project.prj_area_or_location && project.prj_city
                                ? `${project.prj_area_or_location}, ${project.prj_city}`
                                : "Address not mentioned"}
                        </Typography>

                    </Box>
                    <Box sx={{ display: "flex", gap: 4, mt: 2 }}>
                        <PropertyStat icon={Bed} value={project.prj_type ? project.prj_type : "N/A"} />
                        <PropertyStat icon={Bath} value={project.prj_bathroom ? project.prj_bathroom : "N/A"} />
                        <PropertyStat icon={Square} value={project.prj_area + " " + project.prj_areaunit} />
                    </Box>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ pb: 3 }}>
                    <Typography variant="h6" fontWeight="bold" mb={2}>
                        About the Property
                    </Typography>
                    <Typography variant="body1" color="text.secondary" lineHeight={1.6} mb={3}>
                        {project.prj_description || "N/A"}
                    </Typography>

                    {project.project_files?.brochure?.length > 0 && (
                        <Box mb={3}>
                            <Typography variant="h6" fontWeight="bold" mb={1}>
                                Brochure
                            </Typography>
                            <Button
                                variant="contained"
                                href={`${globalData.API_URL}/${project.project_files.brochure[0].file_path}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Download Brochure
                            </Button>
                        </Box>
                    )}


                    {project.prj_video && (
                        <Box mb={3}>
                            <Typography variant="h6" fontWeight="bold" mb={1}>
                                Project Video
                            </Typography>
                            <Button
                                variant="contained"
                                href={project.prj_video}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => {
                                    console.log("[ProjectDetails] Video link clicked:", project.prj_video);
                                }}
                            >
                                Watch Video
                            </Button>
                        </Box>
                    )}

                    {project.project_files?.floor_plan?.length > 0 && (
                        <Box mb={3}>
                            <Typography variant="h6" fontWeight="bold" mb={1}>
                                Floor Plans
                            </Typography>
                            <Grid container spacing={2}>
                                {project.project_files.floor_plan.map((plan, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                        <CardMedia
                                            component="img"
                                            height="150"
                                            image={`${globalData.API_URL}/${plan.file_path}`}
                                            alt={`Floor Plan ${index + 1}`}
                                            sx={{
                                                objectFit: "cover",
                                                borderRadius: 1,
                                                cursor: "pointer",
                                            }}
                                            onClick={() => {
                                                window.open(`${globalData.API_URL}/${plan.file_path}`, "_blank");
                                            }}
                                        />
                                        <Typography variant="body2" mt={1}>
                                            {plan.file_name}
                                        </Typography>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}

                    {project.prj_configurations?.length > 0 && (
                        <Box mb={3}>
                            <Typography variant="h6" fontWeight="bold" mb={1}>
                                Configurations
                            </Typography>
                            <Grid container spacing={2}>
                                {project.prj_configurations.map((config, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                        <Card variant="outlined" sx={{ p: 2 }}>
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                {config.category}
                                            </Typography>
                                            <Typography variant="body2">
                                                {config.from_area} - {config.to_area} {config.aunit}
                                            </Typography>
                                            <Typography variant="body2">
                                                Price: ₹{config.from_price} - ₹{config.to_price} Cr
                                            </Typography>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}

                    {project.prj_amenities?.length > 0 && (
                        <Box mb={3}>
                            <Typography variant="h6" fontWeight="bold" mb={1}>
                                Amenities
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {project.prj_amenities.map((amenity, index) => (
                                    <Chip key={index} label={amenity} variant="outlined" size="small" />
                                ))}
                            </Box>
                        </Box>
                    )}

                    <Box mb={3}>
                        <Typography variant="h6" fontWeight="bold" mb={1}>
                            Project Specifications
                        </Typography>
                        <Grid container spacing={2}>
                            {[
                                ["Structure", project.prj_structure],
                                ["Lifts", project.prj_lift],
                                ["Doors and Windows", project.prj_d_and_w],
                                ["Bathroom", project.prj_bathroom],
                                ["Lobby", project.prj_lobby],
                                ["Flooring", project.prj_flooring],
                                ["Wall Finish", project.prj_wall_finish],
                                ["Kitchen and Utility", project.prj_kitchen_and_u],
                            ].map(([label, value], i) => (
                                <Grid item xs={12} sm={6} md={4} key={i}>
                                    <Typography variant="body2" color="text.secondary">
                                        {label}
                                    </Typography>
                                    <Typography variant="body1">{value || "N/A"}</Typography>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>

                    <Box mb={3}>
                        <Typography variant="h6" fontWeight="bold" mb={1}>
                            Nearby Locations
                        </Typography>
                        <Grid container spacing={2}>
                            {[
                                ["Railway Station", project.prj_railwayd],
                                ["Airport", project.prj_airportd],
                                ["Hospital", project.prj_hospitald],
                                ["Market", project.prj_marketd],
                                ["School", project.prj_schoold],
                                ["Bank", project.prj_bankd],
                                ["ATM", project.prj_atmd],
                            ].map(([label, value], i) => (
                                <Grid item xs={12} sm={6} md={4} key={i}>
                                    <Typography variant="body2" color="text.secondary">
                                        {label}
                                    </Typography>
                                    <Typography variant="body1">{value || "N/A"}</Typography>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>

                    <Divider sx={{ mb: 3 }} />
                    <Typography variant="h6" fontWeight="bold" mb={1}>
                        Details
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        The property's essential info
                    </Typography>
                    <Grid container spacing={3}>
                        {[
                            { label: "Project Type", value: project.prj_type },
                            { label: "Availability", value: project.prj_availability },
                            { label: "Status", value: project.status },
                            { label: "Starting Price", value: `₹${project.prj_start_price} Cr`, highlight: true },
                            { label: "RERA Number", value: project.prj_rera },
                            { label: "Possession", value: `${project.prj_poss_month} ${project.prj_poss_year}` },
                        ].map((detail, i) => (
                            <Grid item xs={12} md={4} key={i}>
                                <DetailItem {...detail} />
                            </Grid>
                        ))}
                    </Grid>

                    <Box mt={4}>
                        <Typography variant="h6" fontWeight="bold" mb={2}>
                            Contact Information
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                    Contact Person
                                </Typography>
                                <Typography variant="body1">
                                    {project.prj_person_name || "N/A"}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                    Email
                                </Typography>
                                <Typography variant="body1">
                                    {project.prj_email_id || "N/A"}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            </Card>
        </Box>
    );
};

export default PublicProjectPage;
